import type { InboxConversation } from "../../types";
import { formatDuration, formatRelativeTime } from "./inboxModel";

/**
 * Whether Felix is replying to a WhatsApp contact, as the bot sees it (FHC-011).
 *
 * The inbox state says who on the team is looking at a conversation; the bot
 * fields say whether Felix is actually holding back. A person's claim is a
 * lease: it lapses after a stretch with no reply from the team, and the contact
 * can type "continue" to get Felix back.
 */
export type FelixOwnershipStatus =
  | { kind: "paused"; ownerName: string | null; expiresAt: number | null }
  | { kind: "lapsed"; ownerName: string | null }
  | { kind: "requested" }
  | {
      kind: "resumed";
      reason: string | null;
      action: string | null;
      at: string | null;
    }
  | { kind: "case_open" }
  | { kind: "case_resolved" };

function time(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function felixOwnershipStatus(
  row: InboxConversation,
  now: number = Date.now(),
): FelixOwnershipStatus | null {
  const kind = row.felix_owner_kind;
  if (kind === "person") {
    const ownerName = row.attention_claimed_by_name || null;
    const expiresAt = time(row.felix_claim_expires_at);
    if (expiresAt != null && expiresAt <= now) {
      return { kind: "lapsed", ownerName };
    }
    return { kind: "paused", ownerName, expiresAt };
  }
  if (kind === "requested") return { kind: "requested" };
  if (kind === "none" && row.felix_ownership_changed_at) {
    return {
      kind: "resumed",
      reason: row.felix_ownership_reason || null,
      // A CRM release arrives as action "release" with reason "operator".
      action: row.felix_ownership_action || null,
      at: row.felix_ownership_changed_at,
    };
  }
  // Rows projected before the bot published ownership changes.
  if (!row.felix_handoff_case_ids?.length) return null;
  return row.attention_state === "resolved"
    ? { kind: "case_resolved" }
    : { kind: "case_open" };
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const RESUMED: Record<string, string> = {
  claim_expired: "Felix is back: the claim lapsed",
  user_resumed: "Felix is back: the contact typed continue",
  release: "Felix is back: the claim was released",
  operator_release: "Felix is back: the claim was released",
};

/** One plain-English line for the inbox row and the contact banner. */
export function felixOwnershipText(
  status: FelixOwnershipStatus,
  now: number = Date.now(),
): string {
  switch (status.kind) {
    case "paused": {
      const by = status.ownerName ? ` by ${status.ownerName}` : "";
      const until =
        status.expiresAt != null
          ? ` until ${clock(status.expiresAt)} (${formatDuration(status.expiresAt - now)} left)`
          : "";
      return `Felix is paused for this contact${by}${until}. Release or resolve to hand back sooner.`;
    }
    case "lapsed":
      return `${status.ownerName ? `${status.ownerName}'s` : "The"} claim has lapsed. Felix answers the contact's next message.`;
    case "requested":
      return "The contact asked for a person. Felix sends a holding reply until someone claims or resolves this.";
    case "resumed": {
      const ago = formatRelativeTime(status.at, now);
      const lead =
        (status.reason && RESUMED[status.reason]) ||
        (status.action && RESUMED[status.action]) ||
        null;
      if (!lead) return "Felix can reply to new messages.";
      return ago
        ? `${lead} (${ago}${ago === "just now" ? "" : " ago"}).`
        : `${lead}.`;
    }
    case "case_open":
      return "Felix is paused. Release or resolve to hand the conversation back to Felix.";
    case "case_resolved":
      return "Resolved. Felix can reply to new messages.";
  }
}

/**
 * The teammate whose claim a release would end, when it is not the current
 * operator. Releasing someone else's claim needs a confirmation and `force`.
 */
export function releaseHolder(
  row: InboxConversation,
  operatorName: string | null | undefined,
): string | null {
  if (row.attention_state !== "claimed") return null;
  const holder = row.attention_claimed_by_name?.trim() || "a teammate";
  if (operatorName && holder === operatorName.trim()) return null;
  return holder;
}
